import { create } from 'zustand';
import { apiFetch } from '../lib/api';
import { CATALOGO as CATALOGO_STATICO } from '../lib/rbacRoles';

// I permessi di accesso della persona collegata.
//
// Non stanno nel token: 50 codici lo gonfiano e — peggio — resterebbero veri
// per i quindici minuti successivi a una revoca. Si leggono da
// `/permessi/me`, che li risolve a ogni chiamata a partire da ruolo, moduli,
// profili ed eccezioni.
//
// Il catalogo (codice → percorso) arriva dallo stesso servizio: è così che
// `ProtectedRoute` sa a quale permesso corrisponde la pagina che si sta per
// aprire, senza che nessuno debba ricopiare cinquanta codici nel router.
//
// Deliberatamente NON persistito: un insieme di permessi salvato nel browser
// sopravvive alla revoca e riappare al ricaricamento della pagina. Si rilegge
// a ogni avvio, costa una richiesta.

export const usePermessiStore = create((set, get) => ({
  permessi: [],
  negati: [],
  fonte: {},
  catalogo: [],
  versione: null,
  profili: [],
  stato: 'iniziale', // iniziale | caricamento | pronto | errore
  errore: null,

  carica: async ({ silenzioso = false } = {}) => {
    if (get().stato === 'caricamento') return;
    const eraPronto = get().stato === 'pronto';
    if (!silenzioso || !eraPronto) {
      set({ stato: 'caricamento', errore: null });
    }
    try {
      const [mio, catalogo] = await Promise.all([
        apiFetch('/permessi/me'),
        apiFetch('/permessi/catalogo'),
      ]);
      set({
        permessi: mio?.data?.permessi || [],
        negati: mio?.data?.negati || [],
        fonte: mio?.data?.fonte || {},
        versione: mio?.data?.versione || null,
        profili: mio?.data?.profili || [],
        catalogo: catalogo?.data || [],
        stato: 'pronto',
        errore: null,
      });
    } catch (err) {
      if (silenzioso && eraPronto) return;
      // Se non si riescono a leggere i permessi non si finge che siano vuoti
      // (l'utente resterebbe chiuso fuori da tutto) né che ci siano tutti. Lo
      // stato resta "errore" e chi decide — il router — lascia passare in base
      // al solo ruolo, come faceva prima che questo esistesse.
      set({ stato: 'errore', errore: err?.message || 'permessi non leggibili' });
    }
  },

  azzera: () => set({ permessi: [], negati: [], fonte: {}, catalogo: [], versione: null, profili: [], stato: 'iniziale', errore: null }),
}));

// Il permesso associato a un percorso, scegliendo la voce di catalogo più
// specifica che lo prefissa: `/helpdesk/board` deve rispondere alla Board e
// non alla Coda (`/helpdesk`), che pure lo prefissa.
export function permessoPerPercorso(catalogo, percorso) {
  const sorgente = Array.isArray(catalogo) && catalogo.length > 0 ? catalogo : CATALOGO_STATICO;
  if (!percorso) return null;
  let migliore = null;
  for (const voce of sorgente) {
    if (!voce.percorso) continue;
    if (percorso === voce.percorso || percorso.startsWith(`${voce.percorso}/`)) {
      if (!migliore || voce.percorso.length > migliore.percorso.length) migliore = voce;
    }
  }
  return migliore ? migliore.codice : null;
}

// Da usare nei componenti: `const puo = usaPermessi(); puo('vista.iam.audit')`.
// Finché i permessi non sono pronti risponde `false` (fail-closed).
export function usaPermessi() {
  const { permessi, stato } = usePermessiStore();
  return (codice) => {
    if (!codice) return true;
    if (stato !== 'pronto') return false;
    return permessi.includes(codice);
  };
}
